import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { GENRE_FAMILIES } from "@/config/genres";

export function GenrePicker({ selected = [], onChange, dark = false }) {
  const [expandedFamily, setExpandedFamily] = useState(null);

  const toggle = (genre) => {
    if (selected.includes(genre)) {
      onChange(selected.filter((g) => g !== genre));
    } else {
      onChange([...selected, genre]);
    }
  };

  const baseBg = dark ? "bg-foreground/5" : "bg-muted";
  const baseBorder = dark ? "border-foreground/10" : "border-border";
  const baseText = dark ? "text-muted-foreground" : "text-muted-foreground";
  const titleText = dark ? "text-foreground" : "text-card-foreground";

  return (
    <div className="space-y-1.5" data-testid="genre-picker">
      {Object.entries(GENRE_FAMILIES).map(([family, genres]) => {
        const isExpanded = expandedFamily === family;
        const selectedInFamily = genres.filter((g) => selected.includes(g));
        return (
          <div key={family} className={`rounded-xl border ${baseBorder} overflow-hidden`}>
            <button
              type="button"
              onClick={() => setExpandedFamily(isExpanded ? null : family)}
              className={`w-full flex items-center justify-between px-3 py-2 ${baseBg} hover:opacity-80 transition-opacity`}
              data-testid={`genre-family-${family}`}
            >
              <span className={`text-xs font-bold ${titleText}`}>
                {family}
                {selectedInFamily.length > 0 && (
                  <span className="ml-1.5 text-[10px] text-[#FF6B35] font-mono-credits">
                    ({selectedInFamily.length})
                  </span>
                )}
              </span>
              {isExpanded ? (
                <ChevronUp className={`w-3.5 h-3.5 ${baseText}`} />
              ) : (
                <ChevronDown className={`w-3.5 h-3.5 ${baseText}`} />
              )}
            </button>
            {isExpanded && (
              <div className="px-3 py-2 flex flex-wrap gap-1.5">
                {genres.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => toggle(g)}
                    className={`text-[10px] px-2.5 py-1 rounded-full border transition-all ${
                      selected.includes(g)
                        ? "bg-[#FF6B35]/10 border-[#FF6B35]/40 text-[#FF6B35]"
                        : dark
                        ? "bg-foreground/5 border-foreground/10 text-muted-foreground"
                        : "bg-white border-border text-muted-foreground"
                    }`}
                    data-testid={`genre-${g}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function GenreFilter({ selected, onChange, dark = false }) {
  const families = Object.keys(GENRE_FAMILIES);
  const [expandedFamily, setExpandedFamily] = useState(null);

  const handleFamilyClick = (f) => {
    if (expandedFamily === f) {
      setExpandedFamily(null);
      onChange("Tous");
    } else {
      setExpandedFamily(f);
      onChange(f);
    }
  };

  const handleSubGenreClick = (sub) => {
    onChange(sub === selected ? expandedFamily : sub);
  };

  const activeStyle = "bg-[#FF6B35]/10 border-[#FF6B35]/40 text-[#FF6B35]";
  const inactiveStyle = dark
    ? "bg-foreground/5 border-foreground/10 text-muted-foreground"
    : "bg-card border-border text-muted-foreground";
  const subActiveStyle = "bg-[#FF6B35] border-[#FF6B35] text-white";

  return (
    <div data-testid="genre-filter">
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        <button
          onClick={() => { setExpandedFamily(null); onChange("Tous"); }}
          className={`text-xs px-4 py-2 rounded-full whitespace-nowrap border transition-all shrink-0 ${
            selected === "Tous" && !expandedFamily ? activeStyle : inactiveStyle
          }`}
          data-testid="genre-tous"
        >
          Tous
        </button>
        {families.map((f) => (
          <button
            key={f}
            onClick={() => handleFamilyClick(f)}
            className={`text-xs px-4 py-2 rounded-full whitespace-nowrap border transition-all shrink-0 ${
              expandedFamily === f ? activeStyle : inactiveStyle
            }`}
            data-testid={`genre-family-${f}`}
          >
            {f}
          </button>
        ))}
      </div>
      {expandedFamily && (
        <div className="flex gap-1.5 flex-wrap mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
          {GENRE_FAMILIES[expandedFamily].map((sub) => (
            <button
              key={sub}
              onClick={() => handleSubGenreClick(sub)}
              className={`text-[11px] px-3 py-1.5 rounded-full whitespace-nowrap border transition-all ${
                selected === sub ? subActiveStyle : inactiveStyle
              }`}
              data-testid={`genre-sub-${sub}`}
            >
              {sub}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
